import { Component, MouseEvent, ReactNode } from 'react'
import * as cn from 'classnames'
import * as D3 from 'd3'
import * as clamp from 'lodash/clamp'
import * as throttle from 'lodash/throttle'
import * as sortBy from 'lodash/sortBy'
import * as range from 'lodash/range'
import * as reject from 'lodash/reject'
import * as isNull from 'lodash/isNull'
import * as isNumber from 'lodash/isNumber'
import * as isEqual from 'lodash/isEqual'
import * as set from 'lodash/set'
import { generate } from 'randomstring'
import * as styles from 'components/features/pfm/LineChart/LineChart.css'
import { AnyDict, NumericMap, Omit } from 'interfaces'
import { ChartAxisScales, ScaleFunction, SecondaryLine, ValueDecorator } from 'interfaces/chart'
import { formatRank, formatUSCurrency } from 'helpers/formatHelpers'
import { doElementsVerticallyIntersect, getClassSelector } from 'helpers/styleHelpers'
import { DEFAULT_LINE_CHART_SCALES, LINEAR_SCALE } from 'constants/chart'
import { rescale } from 'helpers/trendHelpers'
import ChartHeader, { ChartHeaderItem, ChartHeaderProps } from 'components/atoms/charts/ChartHeader'
import LineChartLegend, { LineLegend } from 'components/atoms/legends/LineChartLegend'
import { CHART_LABELS_TILT_THRESHOLD } from 'constants/component'
import HeadedChartLayout, { HeadedChartLayoutComponent } from 'components/layouts/HeadedChartLayout'
import { createTouchMoveXHandler } from 'helpers/touchHelpers'
import Touchable from 'components/utils/Touchable'
import { IS_MOBILE } from 'helpers/userAgentHelpers'

type MemberLineType = 'member'
export type PeersLineType = 'peers' | 'others'
export type LineType = MemberLineType | PeersLineType

export type Item = {
  x: number
  y: number
}

export type Dot = {
  x: number
  y: number
  type: LineType
  // tooltipType equals to type if not defined
  tooltipType?: LineType
}

type Point = [number, number]

export type TooltipDirection = 'up' | 'down'
export type Tooltip = {
  type: LineType
  text: string | number
  x: number
  y: number
  // 'up' by default, now works only for default tooltips
  direction?: TooltipDirection
}

type MinAxisValues = Record<keyof ChartAxisScales, number>

// TODO @pmyagkov once the rank containers are merged move this `min` logic to the final container
const preparePoints = (items: Item[], { x: minX, y: minY }: MinAxisValues): Point[] =>
  items.map(({ x, y }) => [Math.max(x, minX), Math.max(y, minY)] as Point)

const pointsToMap = (points: Point[]): NumericMap<number> =>
  points.reduce<NumericMap<number>>((result, point) => {
    result[point[0]] = point[1]
    return result
  }, {})

const mapToPoints = (map: NumericMap<number>): Point[] =>
  Object.keys(map).map(Number).map((x) => [x, map[x]] as Point)

export type LineChartInnerStateProps = {
  memberItems: Item[]
  peersItems: Item[]
  otherItems: Item[]

  dots: Dot[]

  limitLine?: SecondaryLine
  compareLine?: SecondaryLine

  memberProjectedItems?: Item[]
  peersProjectedItems?: Item[]
  otherProjectedItems?: Item[]

  xWidth: number
  defaultTooltips: Tooltip[]

  // first days of months
  dividers: number[]
  // tips that appear on bottom of hover marker on each point
  tips: NumericMap<string>
  // date tips that in a header
  headerTips: NumericMap<string> | string
  // you can fit it i.e. between dividers
  sections?: NumericMap<string>
  valueDecorator?: ValueDecorator
  selectedPeergroupName: string

  scales?: ChartAxisScales
  xAxisPosition?: number

  isMobile: boolean

  withComparison: boolean
}

export type LineChartInnerOwnProps = {
  fullHeight?: boolean
}

type ShownableValue = {
  x: {
    raw: number
    tip: string
    headerTip: string
  }
  member?: number
  peers?: number
  others?: number
  kind?: LineType

  isProjected: boolean
}

export type LineChartInnerProps =
  & LineChartInnerStateProps
  & LineChartInnerOwnProps
  & {
    onShowValues?: (values: ShownableValue[], isDefault: boolean) => void
  }

const TIP_OFFSET = 18
const SECTIONS_OFFSET = 40
const X_STOP_HEIGHT = 6 // pixels
const AREA_OFFSET = 1
const LINE_OFFSET = 5

const EASING_TIME = 350
const EASING_TYPE = 'quad'
const DOT_MOBILE = {
  radius: 5,
  stroke: 1,
}
const DOT = {
  radius: 5,
  stroke: 1,
}
const AXIS_HEIGHT = 30
const DEFAULT_TOOLTIP_DEFERMENT = EASING_TIME + 50
const TILT_SECTION_ANGLE = -45
const TAP_INTERACTION_DELAY = 250

export const GRADIENT_COLORS = {
  you: 'rgb(73, 90, 185)',
  peers: 'rgb(248, 132, 132)',
  others: 'rgb(176, 96, 120)',
}

type LineChartInnerState = {
  that: LineChartInner

  points: Record<LineType, Point[]>
  projected: Record<LineType, Point[]>

  // for actual & projected both
  // these are for best UX with hoverMarker and tooltips
  memberPointsMap: NumericMap<number>

  // default tooltips with fixed direction
  defaultTooltips: Tooltip[]

  // dots for actual & projected both
  dots: Record<LineType, Point[]>
  dotMaps: Record<LineType, NumericMap<number>>

  interpolated: Record<PeersLineType, Point[]>
  interpolatedProjected: Record<PeersLineType, Point[]>
  // for actual & projected both
  interpolatedMaps: Record<PeersLineType, NumericMap<number>>

  minValue: number
  maxValue: number
  maxValues: NumericMap<number>
  maxValuesPoints: Point[]

  shouldTiltSections: boolean
}

// type for prevent false strictNullChecks error with optional props that covered by defaultProps
type LineChartInnerDefaultProps =
  & LineChartInnerProps
  & typeof LineChartInner.defaultProps

// TODO the component should definitely be decomposed
class LineChartInner extends Component<LineChartInnerProps, LineChartInnerState> {
  static defaultProps = {
    memberProjectedItems: [],
    peersProjectedItems: [],
    otherProjectedItems: [],
    sections: {} as NumericMap<string>,
    valueDecorator: formatUSCurrency,
    defaultTooltips: [] as Tooltip[],
    scales: DEFAULT_LINE_CHART_SCALES,
    xAxisPosition: 0,
  }

  static getDerivedStateFromProps (nextProps: LineChartInnerDefaultProps, prevState: LineChartInnerState) {
    const nextState = prevState.that.calculateState(nextProps)
    return nextState
  }

  private lastHoveredX: number | null

  private svg: D3.Selection<void>
  private easingTime: number = EASING_TIME

  private x: ScaleFunction
  private y: ScaleFunction
  private width: number
  private height: number

  // This functions turn dataset to geometry line
  private roundLine: D3.svg.Line<Point>
  private linearLine: D3.svg.Line<Point>

  // This functions turn dataset to geometry area
  private roundArea: D3.svg.Area<Point>
  private linearArea: D3.svg.Area<Point>
  // maxValueArea resolves focus-losing issue between area & line
  private maxValueArea: D3.svg.Area<Point>

  private chartContainerNode: HTMLDivElement | null = null
  private chartNode: SVGElement | null = null
  private activeArea: SVGElement | null = null
  private lines: SVGGElement | null = null
  private dividers: SVGGElement | null = null
  private captions: SVGGElement | null = null
  private tip: SVGTextElement | null = null

  private defaultHoverMarkerNode: SVGElement | null = null
  private defaultTipNode: SVGElement | null = null

  private majorLimitLabel: HTMLDivElement | null = null
  private minorLimitLabel: HTMLDivElement | null = null

  private touchMoveInitiated = false

  private get shouldShowLineLabels () {
    const { limitLine, compareLine, isMobile } = this.props

    const hasLimits = Boolean(limitLine || compareLine)
    return hasLimits && !isMobile
  }

  private get dotRadius () {
    return this.props.isMobile ? DOT_MOBILE.radius : DOT.radius
  }

  private get dotStrokeWidth () {
    return this.props.isMobile ? DOT_MOBILE.stroke : DOT.stroke
  }

  constructor (props: LineChartInnerDefaultProps) {
    super(props)

    // Populate the initial data required by render
    this.state = {
      ...this.calculateState(props),
      that: this,
    }
  }

  render () {
    const {
      dividers, sections, xWidth,
      limitLine, compareLine,
      tips,
    } = this.props as LineChartInnerProps
    const { projected, dots, defaultTooltips } = this.state

    const idPostfix = generate()

    return (
      <div className={ styles.lineChart }>
        { this.shouldShowLineLabels && (
          <div className={ styles.limits }>
            { limitLine && this.renderLineLabel(limitLine, true) }
            { compareLine && this.renderLineLabel(compareLine, false) }
          </div>
        )}
        <div
          className={ styles.chartContainer }
          ref={ (node) => this.chartContainerNode = node }
        >
          <Touchable
            childRef={ (node: SVGGElement | null) => this.chartNode = node }
            onTap={ this.handleTap }
          >
            <svg
              className={ styles.chart }
              onMouseMove={ IS_MOBILE ? undefined : this.handleChartMouseMove }
              onMouseLeave={ IS_MOBILE ? undefined : this.handleChartMouseLeave }
            >
              <defs>
                <linearGradient id={ `member-gradient${ idPostfix }` } x1='0' y1='0' x2='0' y2='1' spreadMethod='pad'>
                  <stop offset='0%' stopColor={ GRADIENT_COLORS['you'] } stopOpacity='0.3' />
                  <stop offset='100%' stopColor={ GRADIENT_COLORS['you'] } stopOpacity='0.1' />
                </linearGradient>
                <linearGradient id={ `peers-gradient${ idPostfix }` } x1='0' y1='0' x2='0' y2='1' spreadMethod='pad'>
                  <stop offset='0%' stopColor={ GRADIENT_COLORS['peers'] } stopOpacity='0.3' />
                  <stop offset='100%' stopColor={ GRADIENT_COLORS['peers'] } stopOpacity='0.1' />
                </linearGradient>
                <linearGradient id={ `other-gradient${ idPostfix }` } x1='0' y1='0' x2='0' y2='1' spreadMethod='pad'>
                  <stop offset='0%' stopColor={ GRADIENT_COLORS['others'] } stopOpacity='0.3' />
                  <stop offset='100%' stopColor={ GRADIENT_COLORS['others'] } stopOpacity='0.1' />
                </linearGradient>
              </defs>
              <g ref={ (el: SVGGElement | null) => this.lines = el }>
                { projected.member.length > 0 && <path className={ styles.memberProjectionArea } /> }
                <path className={ styles.memberArea } fill={ `url(#member-gradient${ idPostfix })` } />
                <path className={ styles.maxValuesArea } ref={ (el) => this.activeArea = el } />

                <path className={ styles.axisX } />
                <g ref={ (el) => this.dividers = el }>
                  { dividers.map((x) => {
                    /**
                      * `width`, `height` and `transform` doesn't work from the CSS in IE/Edge
                      */
                    const DIVIDER_WIDTH = 2
                    const transform = x === 1 // the first divider
                      ? undefined
                      : (x === xWidth
                        ? `translate(-${ DIVIDER_WIDTH }, 0)`
                        : `translate(-${ DIVIDER_WIDTH / 2 }, 0)`
                      )

                    return (
                      <rect
                        key={ String(x) }
                        className={ styles.divider }
                        x={ 0 }
                        y={ 0 }
                        width={ DIVIDER_WIDTH }
                        height={ 6 }
                        transform={ transform }
                      />
                    )
                  }) }
                </g>

                <path className={ styles.hoverMarker } />

                <g
                  ref={ (el) => this.defaultHoverMarkerNode = el }
                  className={ styles.defaultHoverMarkers }
                  data-qa-marker='hover-scrubber'
                >
                  { defaultTooltips.map((_tooltip, idx) => (
                    <path key={ idx } className={ styles.defaultHoverMarker } data-idx={ idx } />
                  )) }
                </g>

                { limitLine && this.renderLine(limitLine, true) }
                { compareLine && this.renderLine(compareLine, false) }

                { this.props.withComparison && (
                  <path
                    className={ styles.otherLine }
                    data-line-type='other'
                  />
                )}
                { this.props.withComparison && projected.others.length > 0 && (
                  <path
                    className={ styles.otherProjectionLine }
                    data-line-type='other'
                  />
                )}
                { this.props.withComparison && (
                  <path
                    className={ styles.peersLine }
                    /* TODO resp; remove */
                    data-line-type='peers'
                  />
                )}
                { this.props.withComparison && projected.peers.length > 0 && (
                  <path
                    className={ styles.peersProjectionLine }
                    data-line-type='peers'
                  />
                )}
                <path className={ styles.memberLine } />
                { projected.member.length > 0 && <path className={ styles.memberProjectionLine } /> }
                <g>
                  { /* We need hidden dots for emulating z-indexes */ }
                  { this.props.withComparison && this.renderDots(styles.otherDotHidden, 'others', dots.others) }
                  { this.props.withComparison && this.renderDots(styles.peersDotHidden, 'peers', dots.peers) }
                  { this.renderDots(styles.memberDotHidden, 'member', dots.member) }
                  { this.props.withComparison && this.renderDots(styles.otherDot, 'others', dots.others) }
                  { this.props.withComparison && this.renderDots(styles.peersDot, 'peers', dots.peers) }
                  { this.renderDots(styles.memberDot, 'member', dots.member) }
                </g>
              </g>
              <g ref={ (el) => this.captions = el }>
                { sections && (
                  <g transform={ `translate(0 ${ SECTIONS_OFFSET })` }>
                    { Object.keys(sections).map((x) => (
                      <text
                        key={ x }
                        className={ styles.section }
                        textAnchor='middle'
                      >
                        { sections[Number(x)] }
                      </text>
                    )) }
                  </g>
                )}
                <g ref={ (el) => this.defaultTipNode = el } className={ styles.defaultTips } data-qa-marker='line-chart-tip'>
                  { defaultTooltips.map((tooltip, idx) => (
                    <text
                      key={ idx }
                      className={ styles.defaultTip }
                      transform='translate(0 0)'
                      textAnchor='middle'
                      y={ TIP_OFFSET }
                    >
                      { tips[tooltip.x] }
                    </text>
                  )) }
                </g>
                <text
                  className={ styles.tip }
                  textAnchor='middle'
                  ref={ (el) => this.tip = el }
                  y={ TIP_OFFSET }
                />
              </g>
            </svg>
          </Touchable>
        </div>
      </div>
    )
  }

  renderDots (className: string, type: LineType, dots: Point[]) {
    return dots.map((dot, idx: number) => (
      <circle
        className={ className }
        key={ idx }
        r={ this.dotRadius }
        strokeWidth={ this.dotStrokeWidth }
        data-dot-type={ type }
        data-x={ dot[0] }
      />
    ))
  }

  renderLineLabel (limit: SecondaryLine, isMajor: boolean) {
    const className = cn({
      [styles.majorLimitLabel]: isMajor,
      [styles.minorLimitLabel]: !isMajor,
    })

    return (
      <div
        className={ className }
        ref={ (el) => isMajor ? this.majorLimitLabel = el : this.minorLimitLabel = el }
      >
        <section className={ styles.limitTitle }>
          { limit.title }:
        </section>
        <section className={ styles.limitCaption }>
          { limit.amountCaption || formatUSCurrency(limit.amount) }
        </section>
      </div>
    )
  }

  renderLine (line: SecondaryLine, isMajor: boolean) {
    const className = cn({
      [styles.majorLimitLine]: isMajor,
      [styles.minorLimitLine]: !isMajor,
      [styles.memberLine]: line.kind === 'member',
    })

    return (
      <line className={ className } />
    )
  }

  componentDidMount () {
    if (!this.chartNode || !this.lines) {
      return
    }

    this.svg = D3.select(this.chartNode)
    this.update()
    this.showDefaultMarkerThrottled()

    window.addEventListener('resize', this.handleResize)
    this.chartNode.addEventListener('touchmove', this.handleTouchMove, { passive: false } as any)
    this.chartNode.addEventListener('touchend', this.handleTouchEnd)
  }

  shouldComponentUpdate (nextProps: LineChartInnerProps, nextState: LineChartInnerState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState)
  }

  componentDidUpdate () {
    this.preventDefaultMarkers()

    // Update the D3 points after we've used React to re-render
    this.update()
    this.showDefaultMarkerThrottled()
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
    this.preventDefaultMarkers()
    this.hideDefaultMarkers()

    if (this.chartNode) {
      this.chartNode.removeEventListener('touchmove', this.handleTouchMove, { passive: false } as any)
      this.chartNode.removeEventListener('touchend', this.handleTouchEnd)
    }
  }

  // non-UI data
  private calculateState (props: LineChartInnerDefaultProps) {
    const {
      xWidth, dots,
      memberItems, peersItems, otherItems,
      memberProjectedItems, peersProjectedItems, otherProjectedItems,
      limitLine, compareLine, defaultTooltips,
      scales,
      withComparison,
    } = props

    const minValues: Record<keyof ChartAxisScales, number> = {
      x: (scales as ChartAxisScales).x === 'log' ? 1 : Number.NEGATIVE_INFINITY,
      y: (scales as ChartAxisScales).y === 'log' ? 1 : Number.NEGATIVE_INFINITY,
    }

    // items -> d3 points
    const points = {
      member: preparePoints(memberItems, minValues),
      peers: preparePoints(peersItems, minValues),
      others: preparePoints(otherItems, minValues),
    }

    const projected = {
      member: preparePoints(memberProjectedItems, minValues),
      peers: preparePoints(peersProjectedItems, minValues),
      others: preparePoints(otherProjectedItems, minValues),
    }

    // fat dots
    const buildDots = (type: LineType) =>
      dots.filter((dot) => dot.type === type).map(({ x, y }) => [x, y] as Point)
    const dotsByType = {
      member: buildDots('member'),
      peers: buildDots('peers'),
      others: buildDots('others'),
    }

    const memberPointsMap = points.member
      .concat(projected.member)
      .reduce((result, [x, y]) => ({
        ...result,
        [x]: y,
      }), {} as NumericMap<number>)

    const dotMaps = {
      member: pointsToMap(dotsByType.member),
      peers: pointsToMap(dotsByType.peers),
      others: pointsToMap(dotsByType.others),
    }

    // interpolated
    const {
      interpolated, interpolatedProjected, interpolatedMaps,
    } = this.calculateInterpolated(points, projected)

    // cache the boundary values for range calculations
    let apv = [
      ...memberItems,
      ...memberProjectedItems,
    ]
    if (withComparison) {
      apv = apv.concat(
        peersItems, otherItems,
        peersProjectedItems, otherProjectedItems,
      )
    }
    const allPossibleValues = apv
      .map(({ y }) => y)
      .concat(
        0,
        limitLine ? limitLine.amount : 0,
        compareLine ? compareLine.amount : 0
      )

    const minValue = D3.min(allPossibleValues)
    const maxValue = D3.max(allPossibleValues)

    const filledPointsMaps = withComparison ? [
      memberPointsMap,
      interpolatedMaps.peers,
      interpolatedMaps.others,
    ] : [
      memberPointsMap,
    ]
    const maxValues = this.getMaxValues(xWidth, minValue, filledPointsMaps)
    const maxValuesPoints = Object.keys(maxValues).map((x) => [Number(x), maxValues[Number(x)]] as Point)

    const defaultTooltipsSorted = sortBy(defaultTooltips, (tooltip) => tooltip.x)
    const shouldTiltSections = this.calculateShouldTiltSections()

    return {
      points,
      projected,

      memberPointsMap,

      defaultTooltips: defaultTooltipsSorted,

      dots: dotsByType,
      dotMaps,

      interpolated,
      interpolatedProjected,
      interpolatedMaps,

      minValue,
      maxValue,
      maxValues,
      maxValuesPoints,

      shouldTiltSections,
    }
  }

  private calculateInterpolated (points: Record<LineType, Point[]>, projected: Record<LineType, Point[]>) {
    const peersMap = this.interpolatePoints(points.peers)
    const otherMap = this.interpolatePoints(points.others)

    const peersProjectedMap = this.interpolatePoints(projected.peers)
    const otherProjectedMap = this.interpolatePoints(projected.others)

    const interpolatedMaps = {
      peers: { ...peersMap, ...peersProjectedMap },
      others: { ...otherMap, ...otherProjectedMap },
    }

    const interpolated = {
      peers: mapToPoints(peersMap),
      others: mapToPoints(otherMap),
    }

    const interpolatedProjected = {
      peers: mapToPoints(peersProjectedMap),
      others: mapToPoints(otherProjectedMap),
    }

    return {
      interpolatedMaps,
      interpolated,
      interpolatedProjected,
    }
  }

  private getMaxValues (xWidth: number, minValue: number, maps: NumericMap<number>[]) {
    return range(1, xWidth + 1)
      .reduce((result, x) => {
        const y = D3.max(maps.map((map) => isNumber(map[x]) ? map[x] : minValue))

        result[x] = y
        return result
      }, {} as NumericMap<number>)
  }

  // TODO @rendalf extract this logic
  private interpolatePoints (points: Point[]): NumericMap<number> {
    const len = points.length
    if (len === 0) {
      return {}
    }

    if (len === 1) {
      const [x, y] = points[0]
      return { [x]: y }
    }

    const minX = points[0][0]
    const maxX = points[len - 1][0]

    let prevPointIndex = 0
    let nextPointIndex = 1
    let prevPoint = points[prevPointIndex]
    let nextPoint = points[nextPointIndex]
    let delta = (prevPoint[1] - nextPoint[1]) / (prevPoint[0] - nextPoint[0])
    let lastY = prevPoint[1]

    const result: NumericMap<number> = {}
    for (let x = minX; x <= maxX; x++) {
      if (x === points[nextPointIndex][0]) {
        prevPointIndex = nextPointIndex
        prevPoint = nextPoint

        if (x < maxX) {
          nextPointIndex++
          nextPoint = points[nextPointIndex]

          delta = (prevPoint[1] - nextPoint[1]) / (prevPoint[0] - nextPoint[0])
        }
      }

      lastY = x === prevPoint[0] ?
        prevPoint[1] :
        lastY + delta

      result[x] = lastY
    }

    return result
  }

  private update () {
    if (!this.chartNode || !this.captions || !this.dividers || !this.chartContainerNode) {
      return
    }

    const { dividers, sections, xWidth, scales, xAxisPosition } = this.props as LineChartInnerDefaultProps
    const { x: scaleX, y: scaleY } = scales
    const {
      minValue, maxValue, maxValuesPoints,
      points, projected,
      dots, interpolated,
      shouldTiltSections,
    } = this.state

    const { clientWidth, clientHeight } = this.chartContainerNode
    this.width = clientWidth
    this.height = clientHeight

    this.lastHoveredX = null

    // This is the scaling function that maps date values to x coordinates.
    this.x = typeof scaleX === 'string'
      ? D3.scale[scaleX || LINEAR_SCALE]().domain([1, xWidth]).range([this.dotRadius, this.width - this.dotRadius])
      : rescale([1, xWidth], [this.dotRadius, this.width - this.dotRadius])(scaleX)

    const normalizedMinValue = scaleY === 'log'
      ? (minValue < 1 ? 1 : minValue)
      : minValue

    // This is the scaling function that maps money values to y coordinates.
    this.y = typeof scaleY === 'string'
      ? D3.scale[scaleY || LINEAR_SCALE]().domain([normalizedMinValue, maxValue || 1]).range([AXIS_HEIGHT, this.height - (this.dotRadius * 2)])
      : rescale([normalizedMinValue, maxValue || 1], [AXIS_HEIGHT, this.height - (this.dotRadius * 2)])(scaleY)

    // lines
    this.roundLine = D3.svg.line()
      .interpolate('monotone')
      .x((point) => this.x(point[0]))
      .y((point) => this.height - this.y(point[1] < normalizedMinValue ? normalizedMinValue : point[1]))

    this.linearLine = D3.svg.line()
      .interpolate('linear')
      .x((data) => this.x(data[0]))
      .y((data) => this.height - this.y(data[1] < normalizedMinValue ? normalizedMinValue : data[1]))

    points.member.length > 0 && this.setPath(`path${ getClassSelector(styles.memberLine) }`, this.roundLine(points.member))
    if (this.props.withComparison) {
      points.peers.length > 0 && this.setPath(`path${ getClassSelector(styles.peersLine) }`, this.linearLine(points.peers))
      points.others.length > 0 && this.setPath(`path${ getClassSelector(styles.otherLine) }`, this.linearLine(points.others))
    }

    projected.member.length > 0 && this.setPath(`path${ getClassSelector(styles.memberProjectionLine) }`, this.linearLine(projected.member))
    if (this.props.withComparison) {
      projected.peers.length > 0 && this.setPath(`path${ getClassSelector(styles.peersProjectionLine) }`, this.linearLine(projected.peers))
      projected.others.length > 0 && this.setPath(`path${ getClassSelector(styles.otherProjectionLine) }`, this.linearLine(projected.others))
    }

    // Update the background areas.
    const ZERO_Y = this.height - this.y(xAxisPosition)

    this.roundArea = D3.svg.area()
      .interpolate('monotone')
      .x((d) => this.x(d[0]))
      .y0(ZERO_Y)
      .y1((d) => d[1] > xAxisPosition
        ? Math.min(this.height - (this.y(d[1]) - AREA_OFFSET), ZERO_Y)
        : Math.max(this.height - (this.y(d[1]) + AREA_OFFSET), ZERO_Y))

    this.linearArea = D3.svg.area()
      .interpolate('linear')
      .x((d) => this.x(d[0]))
      .y0(ZERO_Y)
      .y1((d) => d[1] > xAxisPosition
        ? Math.min(this.height - (this.y(d[1]) - AREA_OFFSET), ZERO_Y)
        : Math.max(this.height - (this.y(d[1]) + AREA_OFFSET), ZERO_Y))

    this.maxValueArea = D3.svg.area()
      .interpolate('linear')
      .x((d) => this.x(d[0]))
      .y0(this.height - AXIS_HEIGHT)
      .y1((d) => this.height - this.y(d[1]))

    // update areas
    this.setPath(
      getClassSelector(styles.maxValuesArea),
      this.maxValueArea(maxValuesPoints)
    )
    points.member.length > 0 &&
      this.setPath(
        getClassSelector(styles.memberArea),
        this.roundArea(points.member)
      )
    points.peers.length > 0 &&
      this.setPath(
        getClassSelector(styles.peersArea),
        this.linearArea(interpolated.peers)
      )
    points.others.length > 0 &&
    this.setPath(
      getClassSelector(styles.otherArea),
      this.linearArea(interpolated.others)
    )
    projected.member.length > 0 &&
      this.setPath(getClassSelector(styles.memberProjectionArea), this.roundArea(projected.member))
    projected.peers.length > 0 &&
      this.setPath(getClassSelector(styles.peersProjectionArea), this.roundArea(projected.peers))
    projected.others.length > 0 &&
      this.setPath(getClassSelector(styles.otherProjectionArea), this.roundArea(projected.others))

    // Update ticks & sections
    this.setAttributes(
      getClassSelector(styles.divider),
      dividers,
      { x: this.x }
    )
    const angle = shouldTiltSections ? TILT_SECTION_ANGLE : 0
    this.setAttributes(
      getClassSelector(styles.section),
      Object.keys(sections),
      {
        x: this.x,
        transform: (x: number) => `rotate(${ angle } ${ this.x(x) } 0)`,
      }
    )

    // Position the x-axis & captions the graph.
    this.captions.setAttribute('transform', `translate(0, ${ this.height - AXIS_HEIGHT })`)
    const dividersPosition = this.height - this.y(xAxisPosition)
    this.dividers.setAttribute('transform', `translate(0, ${ dividersPosition })`)
    this.setPath(
      `path${ getClassSelector(styles.axisX) }`,
      this.linearLine([
        [1, xAxisPosition],
        [xWidth, xAxisPosition],
      ])
    )

    this.adjustLimits()
    this.updateDefaultTooltips()
    this.adjustSections()

    // Update the dots.
    const dotPosition = {
      cx: (data: Point) => this.x(data[0]),
      cy: (data: Point) => this.height - this.y(data[1]),
    }
    this.setAttributes(getClassSelector(styles.memberDot), dots.member, dotPosition)
    if (this.props.withComparison) {
      this.setAttributes(getClassSelector(styles.peersDot), dots.peers, dotPosition)
      this.setAttributes(getClassSelector(styles.otherDot), dots.others, dotPosition)
    }

    this.setAttributes(getClassSelector(styles.memberDotHidden), dots.member, dotPosition)
    if (this.props.withComparison) {
      this.setAttributes(getClassSelector(styles.peersDotHidden), dots.peers, dotPosition)
      this.setAttributes(getClassSelector(styles.otherDotHidden), dots.others, dotPosition)
    }
  }

  private adjustSections = () => {
    const shouldTiltSections = this.calculateShouldTiltSections()
    if (shouldTiltSections !== this.state.shouldTiltSections) {
      this.setState({
        shouldTiltSections,
      })
    }
  }

  private adjustLimits = () => {
    const { xWidth, limitLine, compareLine } = this.props

    const limitLinePosition = {
      x1: (_data: SecondaryLine) => LINE_OFFSET,
      x2: (_data: SecondaryLine) => this.x(xWidth),
      y1: (data: SecondaryLine) => this.height - this.y(data.amount),
      y2: (data: SecondaryLine) => this.height - this.y(data.amount),
    }

    if (limitLine) {
      this.setAttributes(getClassSelector(styles.majorLimitLine), [limitLine], limitLinePosition)
      if (this.majorLimitLabel) {
        this.majorLimitLabel.style.top = `${ this.height - this.y(limitLine.amount) }px`
        this.majorLimitLabel.className = cn(styles.majorLimitLabel, { [styles.memberTheme]: limitLine.labelTheme === 'member' })
      }
    }
    if (compareLine) {
      this.setAttributes(getClassSelector(styles.minorLimitLine), [compareLine], limitLinePosition)
      if (this.minorLimitLabel) {
        this.minorLimitLabel.style.top = `${ this.height - this.y(compareLine.amount) }px`
        this.minorLimitLabel.className = styles.minorLimitLabel
      }
    }

    if (limitLine && compareLine && this.majorLimitLabel && this.minorLimitLabel) {
      const isIntersected = doElementsVerticallyIntersect(
        this.majorLimitLabel,
        this.minorLimitLabel,
      )

      if (isIntersected) {
        const isMinorAbove = compareLine.amount > limitLine.amount
        const majorClass = isMinorAbove ? styles.limitLabelMovedDown : styles.limitLabelMovedUp
        this.majorLimitLabel.classList.add(majorClass)
        const minorClass = isMinorAbove ? styles.limitLabelMovedUp : styles.limitLabelMovedDown
        this.minorLimitLabel.classList.add(minorClass)
      }
    }
  }

  private adjustDefaultTips () {
    const { defaultTipNode } = this
    if (!defaultTipNode) {
      return
    }

    const tips = defaultTipNode.querySelectorAll(getClassSelector(styles.defaultTip)) as NodeListOf<SVGTextElement>
    if (tips.length !== 2) {
      return
    }

    // it works because tips are sorted by x
    const [leftTip, rightTip] = Array.from(tips)
    const leftRect = leftTip.getBoundingClientRect()
    const rightRect = rightTip.getBoundingClientRect()
    const intersection = leftRect.right - rightRect.left
    if (intersection > 0) {
      const amend = Math.ceil(intersection / 2)
      leftTip.setAttribute('transform', `translate(${ -amend } 0)`)
      rightTip.setAttribute('transform', `translate(${ amend } 0)`)
    }
  }

  private updateDefaultTooltips () {
    const {
      defaultHoverMarkerNode: markersContainer,
      defaultTipNode: tipsContainer,
    } = this
    if (!markersContainer || !tipsContainer) {
      return
    }

    const { defaultTooltips } = this.state
    defaultTooltips.forEach((tooltip, idx) => {
      const tipNode = tipsContainer.childNodes[idx] as SVGTextElement
      if (!tipNode) {
        return
      }

      const x = this.x(tooltip.x)
      const markerSelector = `${ getClassSelector(styles.defaultHoverMarker) }[data-idx="${ idx }"]`
      this.setMarker(markerSelector, tooltip.x)

      tipNode.setAttribute('x', String(x))
    })

    this.adjustDefaultTips()
  }

  /**
   * Sets the D3 attributes according to the passed-in selector.
   */
  private setAttributes (selector: string, data: any, attributes: AnyDict, suppressAnimation = false) {
    const chainObject = this.svg.selectAll(selector).data(data)
    const updater = suppressAnimation ?
      chainObject :
      chainObject.transition().duration(this.easingTime).ease(EASING_TYPE)

    // Update all the attributes on the chained object.
    Object.keys(attributes).forEach((key) => {
      updater.attr(key, attributes[key])
    })
  }

  /**
   * Shortcut for setAttributes to set 'd' parameter
   */
  private setPath (selector: string, dPath: string, suppressAnimation = false) {
    this.setAttributes(selector, dPath, {
      d: dPath,
    }, suppressAnimation)
  }

  private showDefaultMarker = () => {
    const { defaultHoverMarkerNode, defaultTipNode } = this
    if (!defaultHoverMarkerNode || !defaultTipNode) {
      return
    }

    this.hideHoverMarker()

    defaultHoverMarkerNode.classList.remove(styles.defaultHoverMarkersHidden)
    defaultTipNode.classList.remove(styles.defaultTipsHidden)

    const { defaultTooltips } = this.state
    if (defaultTooltips.length) {
      this.showValues(defaultTooltips.map(({ x, type }) => ({ x, kind: type })), true)
    }
  }

  private showDefaultMarkerThrottled = throttle(this.showDefaultMarker, DEFAULT_TOOLTIP_DEFERMENT)

  private hideDefaultMarkers = () => {
    const { defaultHoverMarkerNode, defaultTipNode } = this
    if (!defaultHoverMarkerNode || !defaultTipNode) {
      return
    }

    defaultHoverMarkerNode.classList.add(styles.defaultHoverMarkersHidden)
    defaultTipNode.classList.add(styles.defaultTipsHidden)
  }

  private preventDefaultMarkers () {
    this.hideDefaultMarkers()
    this.showDefaultMarkerThrottled.cancel()
  }

  private setMarker (selector: string, x: number) {
    const { minValue, maxValues } = this.state
    const line = this.linearLine([
      [x, minValue],
      [x, maxValues[x]],
    ])

    // marker should continue under the chart for 6px
    const [m, l] = line.split('L')
    const [m1, m2] = m.split(',')
    const m2Updated = (Number(m2) + X_STOP_HEIGHT).toString()
    const updatedLine = `${ m1 },${ m2Updated }L${ l }`

    this.setPath(selector, updatedLine, true)
  }

  private hasAnyIntersectionPoint = (x: number) => {
    const { memberPointsMap, interpolatedMaps } = this.state

    const xKey = String(x)

    const hasMemberPoint = memberPointsMap.hasOwnProperty(xKey)
    const hasPeersPoint = interpolatedMaps.peers.hasOwnProperty(xKey)
    const hasOtherPoint = interpolatedMaps.others.hasOwnProperty(xKey)

    return hasMemberPoint || hasPeersPoint || hasOtherPoint
  }

  private prepareShownableValue = (x: number, kind?: LineType): ShownableValue | undefined => {
    const { tips, headerTips, memberItems } = this.props
    const { memberPointsMap, interpolatedMaps } = this.state

    if (!this.hasAnyIntersectionPoint(x)) {
      return undefined
    }

    const headerTip: string = typeof headerTips === 'string' ? headerTips : headerTips[x]

    return {
      x: { raw: x, tip: tips[x], headerTip },
      member: memberPointsMap[x],
      peers: interpolatedMaps.peers[x],
      others: interpolatedMaps.others[x],

      isProjected: x > memberItems.length,
      kind,
    }
  }

  private showValues (points: { x: number, kind?: LineType }[], isDefault: boolean) {
    const { onShowValues } = this.props

    if (!onShowValues) {
      return
    }

    const values = points.reduce<ShownableValue[]>(
      (result, { x, kind }) => {
        const value = this.prepareShownableValue(x, kind)
        return value ? [...result, value] : result
      },
      []
    )
    onShowValues(values, isDefault)
  }

  private showHoverMarker (x: number) {
    const { tip } = this

    if (!tip) {
      return
    }

    this.hideDefaultMarkers()

    const { tips } = this.props
    this.lastHoveredX = x

    const selector = `path${ getClassSelector(styles.hoverMarker) }`
    this.setMarker(selector, x)

    // Also show x-tip
    tip.setAttribute('x', this.x(x).toString())
    tip.innerHTML = tips[x]

    this.showValues([{ x }], false)
  }

  private hideHoverMarker () {
    if (!this.chartNode || !this.tip) {
      return
    }

    this.lastHoveredX = null
    const hoverMarker = this.chartNode.querySelector(`path${ getClassSelector(styles.hoverMarker) }`)
    if (hoverMarker) {
      hoverMarker.removeAttribute('d')
    }
    this.tip.innerHTML = ''
  }

  private calculateShouldTiltSections () {
    if (!this.chartContainerNode) {
      return false
    }

    const { sections } = this.props

    const chartRect = this.chartContainerNode.getBoundingClientRect()
    const sectionsCount = sections ? Object.keys(sections).length : 1
    const shouldTiltSections = chartRect.width / sectionsCount < CHART_LABELS_TILT_THRESHOLD

    return shouldTiltSections
  }

  private getChartX (clientX: number): number {
    if (!this.activeArea) {
      return 0
    }

    const { xWidth } = this.props
    const { left: offsetLeft, width } = this.activeArea.getBoundingClientRect()
    const chartOffset = clientX - offsetLeft
    const chartX = Math.round((xWidth - 1) * chartOffset / width) + 1

    return clamp(chartX, 1, xWidth)
  }

  private handleResize = () => {
    this.hideHoverMarker()

    this.easingTime = 0
    this.update()
    this.easingTime = EASING_TIME

    this.showDefaultMarkerThrottled()
  }

  private handleChartInteraction = (clientX: number) => {
    const hoveredX = this.getChartX(clientX)

    this.preventDefaultMarkers()
    this.hideDefaultMarkers()

    if (hoveredX === this.lastHoveredX) {
      return
    }
    this.lastHoveredX = hoveredX

    if (this.hasAnyIntersectionPoint(hoveredX)) {
      this.showHoverMarker(hoveredX)
    } else {
      this.showDefaultMarkerThrottled()
    }
  }

  private handleTouchMove = (e: TouchEvent) => {
    this.touchMoveInitiated = true

    this.handleTouchMoveRaw(e)
  }

  private handleTouchMoveRaw = createTouchMoveXHandler(this.handleChartInteraction)

  private handleTouchEnd = () => {
    // handle touchend after touchmove only
    if (!this.touchMoveInitiated) {
      return
    }

    this.touchMoveInitiated = false
    this.showDefaultMarkerThrottled()
  }

  private handleTap = (e: HammerInput) => {
    this.handleChartInteraction(e.center.x)

    // restore default marker after noticeable delay
    setTimeout(() => this.showDefaultMarkerThrottled(), TAP_INTERACTION_DELAY)
  }

  private handleChartMouseMove = (e: MouseEvent<SVGGElement>) => {
    this.handleChartInteraction(e.clientX)
  }

  private handleChartMouseLeave = () => {
    this.hideHoverMarker()
    this.showDefaultMarkerThrottled()
  }
}

type LineChartProps = Omit<LineChartInnerProps, 'onShowValues'>
type LineChartState =
  & Omit<ChartHeaderProps, 'title' | 'withComparison'>
  & {
    title: string
  }

const NO_VALUE_STRING = '–'
export class LineChart extends Component<LineChartProps, LineChartState> {
  static MEMBER_LABEL = 'You'
  static PEERS_LABEL = 'Your Peers'

  state: LineChartState = {
    title: 'TODAY',
    member: {
      label: LineChart.MEMBER_LABEL,
      value: NO_VALUE_STRING,
    },
    peers: {
      label: LineChart.PEERS_LABEL,
      value: NO_VALUE_STRING,
    },
    others: {
      label: this.props.selectedPeergroupName,
      value: NO_VALUE_STRING,
    },
  }

  private lineChartChild: LineChartInner | null = null

  private get valueDecorator () {
    return this.props.valueDecorator || String
  }

  private get hasLegend (): boolean {
    const {
      isMobile,
      compareLine,
      limitLine,
    } = this.props

    return isMobile && Boolean(compareLine || limitLine)
  }

  render () {
    const {
      fullHeight,
      withComparison,
    } = this.props

    return (
      <HeadedChartLayout fullHeight={ fullHeight }>
        <HeadedChartLayoutComponent.HeaderMarker>
          <ChartHeader { ...this.state } withComparison={ withComparison } />
        </HeadedChartLayoutComponent.HeaderMarker>
        <HeadedChartLayoutComponent.ChartMarker>
          <LineChartInner
            ref={ (child) => { this.lineChartChild = child } }
            { ...this.props }
            onShowValues={ this.handleShowValues }
          />
        </HeadedChartLayoutComponent.ChartMarker>
        { this.hasLegend && (
          <HeadedChartLayoutComponent.LegendMarker>
            { this.renderLegend() }
          </HeadedChartLayoutComponent.LegendMarker>
        ) }
      </HeadedChartLayout>
    )
  }

  renderSingleValue = (value: number | null | undefined, fallbackValue: ReactNode): ReactNode => {
    return typeof value !== 'undefined'
      ? (isNumber(value) ? this.valueDecorator(value) : '–')
      : fallbackValue
  }

  renderLegend = () => {
    if (!this.hasLegend) {
      return null
    }

    const { compareLine, limitLine } = this.props

    const compareLineLegend: LineLegend | null = compareLine ? {
      ...compareLine,
      style: 'dashed',
    } : null
    const limitLineLegend: LineLegend | null = limitLine ? {
      ...limitLine,
      style: 'solid',
    } : null

    const lines: LineLegend[] = reject([
      compareLineLegend,
      limitLineLegend,
    ], isNull)

    return (
      <LineChartLegend lines={ lines } />
    )
  }

  forceUpdateChart = () => {
    if (this.lineChartChild) {
      this.lineChartChild.forceUpdate()
    }
  }

  private getChartHeaderProps = (value: ShownableValue): LineChartState => {
    const { x, member, peers, others, isProjected } = value

    const title = `${ x.headerTip }${ isProjected ? ' (Projected)' : '' }`
    const chartHeaderProps: LineChartState = {
      title,
      member: {
        label: LineChart.MEMBER_LABEL,
        value: this.renderSingleValue(member, this.state.member.value),
      },
      peers: {
        label: LineChart.PEERS_LABEL,
        value: this.renderSingleValue(peers, this.state.peers.value),
      },
      others: {
        label: this.props.selectedPeergroupName,
        value: this.renderSingleValue(others, this.state.others.value),
      },
    }

    return chartHeaderProps
  }

  private handleShowValues = (values: ShownableValue[]) => {
    const nextState = this.getChartHeaderProps(values[0])

    if (!isEqual(this.state, nextState)) {
      this.setState(nextState)
    }
  }
}

const addPercent: ValueDecorator = (n: number) => `${ n }%`
export class RankChart extends Component<LineChartProps, LineChartState> {
  static MEMBER_LABEL = 'You Avg. Spending'
  static PEERS_NAME = 'Your Peer'

  state: LineChartState = {
    title: this.title,
    member: {
      label: RankChart.MEMBER_LABEL,
      value: NO_VALUE_STRING,
    },
    peers: {
      label: this.getPeergroupLabel('peers'),
      value: NO_VALUE_STRING,
    },
    others: {
      label: this.getPeergroupLabel('others'),
      value: NO_VALUE_STRING,
    },
  }

  private lineChartChild: LineChartInner | null = null

  private get title () {
    const { headerTips } = this.props

    return typeof headerTips === 'string'
      ? headerTips.toUpperCase()
      : 'TODAY'
  }

  private get valueDecorator () {
    return this.props.valueDecorator || String
  }

  private get hasLegend (): boolean {
    const {
      isMobile,
      compareLine,
      limitLine,
    } = this.props
    if (limitLine) {
      limitLine.labelTheme = 'member'
    }
    return isMobile && Boolean(compareLine || limitLine)
  }

  render () {
    const { limitLine, fullHeight, withComparison } = this.props
    return (
      <HeadedChartLayout fullHeight={ fullHeight }>
        <HeadedChartLayoutComponent.HeaderMarker>
          <ChartHeader { ...this.state } twoRows={ false } hideYouValue={ limitLine && limitLine.labelTheme === 'member' } withComparison={ withComparison } />
        </HeadedChartLayoutComponent.HeaderMarker>
        <HeadedChartLayoutComponent.ChartMarker>
          <LineChartInner
            ref={ (child) => { this.lineChartChild = child } }
            { ...this.props }
            onShowValues={ this.handleShowValues }
          />
        </HeadedChartLayoutComponent.ChartMarker>
        {
          this.hasLegend && (
            <HeadedChartLayoutComponent.LegendMarker>
              { this.renderLegend() }
            </HeadedChartLayoutComponent.LegendMarker>
          )
        }
      </HeadedChartLayout>
    )
  }

  renderHeaderValue = (value: string | number | null | undefined, fallbackValue: ReactNode, valueDecorator?: ValueDecorator): ReactNode => {
    if (typeof value === 'undefined') {
      return fallbackValue
    }

    if (isNumber(value)) {
      return valueDecorator ? valueDecorator(value) : this.valueDecorator(value)
    }

    if (typeof value === 'string') {
      return value
    }

    return NO_VALUE_STRING
  }

  renderLegend = () => {
    if (!this.hasLegend) {
      return null
    }

    const { compareLine, limitLine } = this.props

    const compareLineLegend: LineLegend | null = compareLine ? {
      ...compareLine,
      style: 'dashed',
    } : null
    const limitLineLegend: LineLegend | null = limitLine ? {
      ...limitLine,
      style: 'solid',
      kind: 'member',
    } : null

    const lines: LineLegend[] = reject([
      compareLineLegend,
      limitLineLegend,
    ], isNull)

    return (
      <LineChartLegend lines={ lines } />
    )
  }

  componentDidMount () {
    // TODO: eliminate this dirtiness; cause of this - FRO-1229
    setTimeout(() => {
      this.forceUpdateChart()
    }, 1000)
  }

  forceUpdateChart = () => {
    if (this.lineChartChild) {
      this.lineChartChild.forceUpdate()
    }
  }

  private getPeergroupLabel (kind: PeersLineType) {
    const peergroupName = kind === 'others'
      ? this.props.selectedPeergroupName
      : RankChart.PEERS_NAME

    return `${ peergroupName } Rank`
  }

  private getPeergroupHeaderProp = (kind: PeersLineType, value: ShownableValue, valueDecorator?: ValueDecorator): ChartHeaderItem => {
    return {
      label: this.getPeergroupLabel(kind),
      value: this.renderHeaderValue(value.x.raw, this.state[kind].value, valueDecorator),
      secondaryValue: this.renderHeaderValue(value[kind], this.state[kind].secondaryValue || ''),
    }
  }

  private getChartHeaderProps = (values: ShownableValue[], isDefault: boolean): LineChartState | null => {
    if (!values.length) {
      return null
    }

    const peergroupValueDecorator = isDefault ? formatRank : addPercent

    const basicChartHeaderProps: Partial<LineChartState> = {
      title: this.title,
      twoRows: true,
      member: {
        label: LineChart.MEMBER_LABEL,
        value: this.props.limitLine ? this.valueDecorator(this.props.limitLine.amount) : NO_VALUE_STRING,
      },
      peers: this.getPeergroupHeaderProp('peers', values[0], peergroupValueDecorator),
      others: this.getPeergroupHeaderProp('others', values[0], peergroupValueDecorator),
    }

    const chartHeaderProps = values.reduce(
      (result, value) => {
        if (!value.kind || value.kind === 'member') {
          return result
        }

        return {
          ...result,
          [value.kind]: this.getPeergroupHeaderProp(value.kind, value, peergroupValueDecorator),
        }
      },
      basicChartHeaderProps
    )

    return chartHeaderProps as LineChartState
  }

  private handleShowValues = (values: ShownableValue[], isDefault: boolean) => {
    const nextState = this.getChartHeaderProps(values, isDefault)

    if (values.length > 1) {
      // no need to show subtitles if there are two points
      set(nextState, 'peers.secondaryValue', undefined)
      set(nextState, 'others.secondaryValue', undefined)
    }

    if (nextState !== null && !isEqual(this.state, nextState)) {
      this.setState(nextState)
    }
  }
}
